import React from "react";
import { SolutionConstraintTable } from "../../powerFlow/solutionConstraints";
import {
  inputHeader,
  inputCell,
  inputMonoHeader,
  inputMonoCell,
  outputHeader,
  outputCell,
} from "./tableStyles";

interface Props {
  table?: SolutionConstraintTable;
  /**
   * The editor for the symbolic rule that generates these rows — the quantified
   * math, e.g. "def dcpf(l: line, t: timestep): P_line(l, t) == ...".
   * Built by the panel container (which owns the rule set); the grounded table
   * below is one row per (l, t) instance.
   */
  ruleEditor?: React.ReactNode;
}

/**
 * Renders a single class of constraints (e.g. dcpf) as a table: its index
 * columns (edge_id, timestamp, ...), the dual (shadow price), and the
 * constraint definition. Grouping into classes is done by the tree, so there's
 * no tab bar here. Inputs (index columns and the constraint definition) are on
 * the left; the solver's output (the dual) is on the right. The rule that
 * generates the rows sits above them, editable.
 */
const ConstraintTableView: React.FC<Props> = ({ table, ruleEditor }) => {
  if (!table || table.rows.length === 0) {
    // A rule can be disabled, still being typed, or simply ground to nothing
    // (no batteries, say) — the editor stays so it can be fixed or re-enabled.
    return (
      <>
        {ruleEditor}
        <div style={{ padding: "12px", color: "#666", fontStyle: "italic" }}>
          No constraints available
        </div>
      </>
    );
  }

  const hasDual = table.rows.some((row) => row.dual !== undefined);

  const dualHeaderStyle: React.CSSProperties = { ...outputHeader, textAlign: "right" };
  const dualCellStyle: React.CSSProperties = { ...outputCell, textAlign: "right" };

  return (
    <>
      {ruleEditor}
      <table
      style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}
    >
      <thead>
          <tr>
            {table.columns.map((column) => (
              <th key={column} style={inputHeader}>
                {column}
              </th>
            ))}
            <th key="__definition" style={inputMonoHeader}>
              constraint
            </th>
            {hasDual && (
              <th key="__dual" style={dualHeaderStyle}>
                dual
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row) => (
            <tr key={row.name}>
              {table.columns.map((column) => (
                <td key={column} style={inputCell}>
                  {String(row.indices[column] ?? "")}
                </td>
              ))}
              <td style={inputMonoCell}>{row.definition}</td>
              {hasDual && (
                <td style={dualCellStyle}>
                  {row.dual !== undefined ? row.dual.toFixed(3) : "—"}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default ConstraintTableView;
